import { buffers } from "@redux-saga/core";
import moment from "moment";
import {
  actionChannel,
  all,
  call,
  delay,
  flush,
  put,
  race,
  select,
  take,
} from "redux-saga/effects";

import {
  deleteWidgetProperty,
  renameWidgets,
  setSingleWidget,
  renameEmbedProps,
  updateLocalWidgets,
} from "legacy/actions/controlActions";
import {
  pageLoadActionsComplete,
  restartEvaluation,
  startEvaluation,
  stopEvaluation,
} from "legacy/actions/evaluationActions";
import {
  resetWidgetMetaProperty,
  setMetaProp,
  setMetaProps,
} from "legacy/actions/metaActions";
import {
  pageLoadSuccess,
  updateLayout,
  updatePartialLayout,
} from "legacy/actions/pageActions";
import {
  deleteTableRows,
  insertTableRows,
  runEventHandlersError,
  updateTableRows,
} from "legacy/actions/widgetActions";
import { ReduxActionTypes } from "legacy/constants/ReduxActionConstants";
import { getEntityCount } from "legacy/entities/DataTree/DataTreeHelpers";
import { APP_MODE } from "legacy/reducers/types";
import { getAppMode } from "legacy/selectors/applicationSelectors";
import { getUnevaluatedDataTree } from "legacy/selectors/dataTreeSelectors";
import { getIsFetchingPage } from "legacy/selectors/editorSelectors";
import { getWidgets } from "legacy/selectors/entitiesSelector";
import {
  getPageCachedData,
  getWidgetsMeta,
} from "legacy/selectors/sagaSelectors";
// TODO: Stop importing directly, but this is a circular dep otherwise
import type { Channel } from "@redux-saga/types";

import type { ReduxAction } from "legacy/constants/ReduxActionConstants";
import type {
  DataTree,
  DataTreeMetaPaths,
} from "legacy/entities/DataTree/dataTreeFactory";
import type { CanvasWidgetsReduxState } from "legacy/reducers/entityReducers/canvasWidgetsReducer";
import type { MetaState } from "legacy/reducers/entityReducers/metaReducer";
import type { SetEvaluatedTreeAction } from "legacy/reducers/evaluationReducers/treeReducer";
import type { WidgetTypeConfigMap } from "legacy/widgets";
import WidgetFactory from "legacy/widgets/Factory";
import renameApplicationEntitySaga from "store/sagas/renameApplicationEntity";
import { AI_ACTIONS_CAUSING_EVALUATION } from "store/slices/ai/slice";
import {
  executeV1ApiSaga,
  updateV1ApiSaga,
  selectAllV1Apis,
  deleteV1ApiSaga,
  clearResponseV1Api,
} from "store/slices/apisShared";
import type { ApiV1 } from "store/slices/apisShared";
import {
  clearResponseV2Api,
  deleteV2ApiSaga,
  executeV2ApiSaga,
  selectAllV2Apis,
  updateV2ApiSaga,
  getV2ApiBlockDepsSaga,
} from "store/slices/apisV2";
import { checkEvaluationNamesDirty } from "store/slices/apisV2/sagas/checkEvaluationNamesDirty";
import { executeV2ApiBlockSaga } from "store/slices/apisV2/sagas/executeV2ApiBlock";
import { callSagas } from "store/utils/saga";
import logger from "utils/logger";
import {
  formatBytes,
  getDetailedSize,
  LOG_STYLES,
  shouldLogPerformance,
} from "utils/performance";

import { updateCurrentRoute } from "../actions/pageActions";
import { EVAL_WORKER_ACTIONS } from "../utils/DynamicBindingUtils";
import type {
  EvalTreeRequest,
  EvalTreeResponse,
  ReplayState,
  ReplayStateOptions,
} from "../utils/DynamicBindingUtils";
import PerformanceTracker, {
  PerformanceTransactionName,
} from "../utils/PerformanceTracker";
import { worker } from "./evaluationLoader";
import { evalErrorHandler } from "./EvaluationsSagaHelpers";

let widgetTypeConfigMap: WidgetTypeConfigMap;

function* evaluateTreeSaga(replayState: ReplayState, isFirstTime?: boolean) {
  const eventName = isFirstTime
    ? PerformanceTransactionName.FIRST_EVALUATION_ON_LOAD
    : PerformanceTransactionName.DATA_TREE_EVALUATION;
  PerformanceTracker.startAsyncTracking(eventName);
  yield put({ type: ReduxActionTypes.START_EVALUATE_TREE });

  const start = performance.now();

  let unevalTree: DataTree;
  let dataTreeMetaPaths: DataTreeMetaPaths;
  try {
    const unevalData: {
      dataTree: DataTree;
      dataTreeMetaPaths: DataTreeMetaPaths;
    } = yield select(getUnevaluatedDataTree);
    unevalTree = unevalData.dataTree;
    dataTreeMetaPaths = unevalData.dataTreeMetaPaths;
  } catch (e: any) {
    throw new Error(`Could not construct dataTree. ${e.message}`);
  }

  let needsStaticAnalysis: boolean;
  const appMode: APP_MODE | undefined = yield select(getAppMode);
  if (!appMode || appMode === APP_MODE.EDIT) {
    // On edit mode, we always need to perform static analysis.
    needsStaticAnalysis = true;
  } else {
    // We are not in edit mode.  We only need to perform a static analysis if we don't have the pageLoad actions cached.
    const cachedData: ReturnType<typeof getPageCachedData> =
      yield select(getPageCachedData);
    needsStaticAnalysis = !cachedData?.pageLoadActions;
  }

  const donePrep = performance.now();

  const request: EvalTreeRequest = {
    unevalTree,
    metaPaths: dataTreeMetaPaths,
    widgetTypeConfigMap: isFirstTime ? widgetTypeConfigMap : undefined,
    replayState,
    options: {
      staticAnalysis: needsStaticAnalysis,
    },
  };

  const response:
    | { responseData: EvalTreeResponse; timeInWorker: string }
    | undefined = yield call(
    worker.requestWithMeta,
    EVAL_WORKER_ACTIONS.EVAL_TREE_REC_EVAL,
    request,
  );
  if (!response?.responseData) {
    // This happens if STOP_EVALUATION is called before the worker responds
    PerformanceTracker.stopAsyncTracking(eventName);
    return;
  }
  const { responseData, timeInWorker } = response;
  const {
    errors,
    dataTree,
    dependencies,
    triggerMap,
    entityDependencyMap,
    entityToApiDepMap,
    logs,
    referenceMeta,
  }: EvalTreeResponse = responseData;

  const doneEval = performance.now();

  logs.forEach((evalLog) => logger.debug(evalLog, undefined, false));

  const doneLog = performance.now();

  evalErrorHandler(errors);

  const doneError = performance.now();

  // Post-evaluation, determine if any entity name has changed for API dep caching
  // we don't care which names are changed, just that there are changes
  const { updateCount, deleteCount } =
    yield checkEvaluationNamesDirty(dataTree);
  const doneMerge = performance.now();

  const isSwitchingPage: ReturnType<typeof getIsFetchingPage> =
    yield select(getIsFetchingPage);

  if (!isSwitchingPage) {
    const { deletedEntities: deletedEntityNames, ...dataTreeUpdates } =
      dataTree;
    // leave a "frozen" state on screen during page switch
    yield put({
      type: ReduxActionTypes.TREE_WILL_UPDATE,
      payload: dataTreeUpdates, // Note that this is the unhydrated tree,
      // this works for now because currently this action only pertains to non-derived properties
      // if the reference manager is modified to operate on arbitrary entity properties, we will need to update this
    });
  }

  const doneUpdate = performance.now();

  if (!isSwitchingPage) {
    // leave a "frozen" state on screen during page switch
    yield put({
      type: ReduxActionTypes.SET_EVALUATED_TREE,
      payload: { dataTree, referenceMeta },
    } satisfies SetEvaluatedTreeAction);
  }

  const doneSet = performance.now();

  yield put({
    type: ReduxActionTypes.SET_EVALUATION_DEPENDENCIES,
    payload: {
      inverseDependencyMap: dependencies,
      actionTriggerMap: triggerMap,
      // entityToApiDepMap is a partial map here, the reducer will merge it with the existing map
      entityToApiDepMap,
      entityDependencyMap,
    },
  });

  const doneAll = performance.now();

  const data = {
    inputChanges:
      getEntityCount(unevalTree) - (unevalTree.deletedEntities?.length ?? 0),
    outputChanges: updateCount + deleteCount,
    timing: {
      a_prep: donePrep - start,
      b_eval: {
        total: doneEval - donePrep,
        on_worker: parseFloat(timeInWorker),
        on_main: doneEval - donePrep - parseFloat(timeInWorker),
      },
      c_log: doneLog - doneEval,
      d_error: doneError - doneEval,
      e_merge: doneMerge - doneError,
      f_treeWillUpdate: doneUpdate - doneMerge,
      g_setTree: doneSet - doneUpdate,
      h_setEvalDep: doneAll - doneSet,
    },
  };

  if (shouldLogPerformance()) {
    const detailedSizes = {
      input: getDetailedSize(unevalTree),
      output: getDetailedSize(dataTree),
      dependencies: getDetailedSize(dependencies),
      triggerMap: getDetailedSize(triggerMap),
    };

    console.groupCollapsed("%cEvaluation Performance", LOG_STYLES.EVALUATION);
    console.log({
      timing: data.timing,
      changes: {
        input: data.inputChanges,
        output: data.outputChanges,
      },
      sizes: Object.fromEntries(
        Object.entries(detailedSizes).map(([key, value]) => [
          key,
          `${value} (${formatBytes(value)})`,
        ]),
      ),
      payloads: {
        input: unevalTree,
        output: dataTree,
        dependencies,
        triggerMap,
        referenceMeta,
      },
    });
    console.groupEnd();
  }

  PerformanceTracker.stopAsyncTracking(eventName, data);
}

export function* reevaluateTree(
  replayOptions: Partial<ReplayStateOptions> | undefined,
): Generator<any, any, any> {
  const widgetsMeta: MetaState = yield select(getWidgetsMeta);
  const currentWidgets: CanvasWidgetsReduxState = yield select(getWidgets);
  const replayState = {
    widgets: currentWidgets,
    shouldReplay: replayOptions?.shouldReplay ?? true,
    clearReplayStack: replayOptions?.clearReplayStack ?? false,
    widgetMeta: widgetsMeta,
  } as ReplayState;

  try {
    yield call(evaluateTreeSaga, replayState);
    yield callSagas([getV2ApiBlockDepsSaga.apply({})]);
  } catch (e: any) {
    logger.error(e);
    yield call(worker.shutdown);
  }
}

const FIRST_EVAL_REDUX_ACTIONS = [
  // Pages
  pageLoadSuccess.type,
];

// actions that nessitate a layout save
const REQUIRES_LAYOUT_SAVE_ACTIONS: string[] = [
  updateLayout.type,
  updatePartialLayout.type,
  setSingleWidget.type,
  renameWidgets.type,
];

const EVALUATE_REDUX_ACTIONS = [
  ...FIRST_EVAL_REDUX_ACTIONS,
  pageLoadActionsComplete.type,
  stopEvaluation.type,
  restartEvaluation.type,
  // Actions
  runEventHandlersError.type,
  // Apis
  executeV1ApiSaga.success.type,
  executeV1ApiSaga.error.type,
  clearResponseV1Api.type,
  updateV1ApiSaga.success.type,
  deleteV1ApiSaga.success.type,
  // Apis V2
  executeV2ApiSaga.success.type,
  executeV2ApiBlockSaga.success.type,
  executeV2ApiBlockSaga.error.type,
  updateV2ApiSaga.success.type,
  deleteV2ApiSaga.success.type,
  clearResponseV2Api.type,
  // App Data
  ReduxActionTypes.SET_APP_MODE,
  ReduxActionTypes.CURRENT_APPLICATION_NAME_UPDATE,
  ReduxActionTypes.FETCH_USER_DETAILS_SUCCESS,
  ReduxActionTypes.UPDATE_APP_STORE,
  // Widgets
  ...REQUIRES_LAYOUT_SAVE_ACTIONS,
  deleteWidgetProperty.type,
  updateLocalWidgets.type,
  ReduxActionTypes.RENAME_WIDGETS,
  // Widget Meta
  setMetaProp.type,
  setMetaProps.type,
  resetWidgetMetaProperty.type,
  // Widget Dynamic Height
  ReduxActionTypes.SET_DYNAMIC_WIDGET_PROPS,
  ReduxActionTypes.CLEAR_DYNAMIC_WIDGET_HEIGHT,
  // Global sagas
  renameApplicationEntitySaga.success.type,
  // tables
  updateTableRows.type,
  deleteTableRows.type,
  insertTableRows.type,
  // Profiles
  ReduxActionTypes.UPDATE_PROFILES,
  // Theme
  ReduxActionTypes.UPDATE_GENERATED_THEME,
  // embedding
  ReduxActionTypes.UPDATE_EMBED_PROPERTY_META,
  ReduxActionTypes.UPDATE_EMBED_PROPERTY,
  ReduxActionTypes.DELETE_EMBED_PROPERTY,
  ReduxActionTypes.CREATE_EMBED_PROPERTY,
  renameEmbedProps.type,
  // multi-page
  ReduxActionTypes.UPDATE_DATA_URL,
  updateCurrentRoute.type,
  // ai
  ...AI_ACTIONS_CAUSING_EVALUATION,
];

// used to prevent first action and second action triggering two evaluation if they are too close (e.g. in a loop that update meta)
const EVALUATE_MINIMUM_DEBOUNCE_DELAY = 10;
const DEFAULT_EVALUATE_DEBOUNCE_DELAY = 100;
const defaultDelays = EVALUATE_REDUX_ACTIONS.reduce(
  (acc, action) => ({ ...acc, [action]: DEFAULT_EVALUATE_DEBOUNCE_DELAY }),
  {} as Record<string, number>,
);

const NO_DEBOUNCE = -1;
const EVALUATE_DEBOUNCE_DELAY = {
  ...defaultDelays,
  [executeV1ApiSaga.success.type]: 10, // A user explicitly runs an api
  [updateV1ApiSaga.success.type]: 300, // Something in our api has changed, when this happens it can happen a lot (such as typing)
  [executeV2ApiSaga.success.type]: 10,
  [executeV2ApiBlockSaga.success.type]: 10,
  [updateV2ApiSaga.success.type]: 300,

  // It's very common that streaming data is updated so we want to minimize the delay
  [ReduxActionTypes.UPDATE_EMBED_PROPERTY_META]: NO_DEBOUNCE,
};

function* evaluationChangeListenerSaga() {
  // Apps seem to send their first action after the worker is ready
  // Jobs & workflows send their first action after
  // To be safe, we need _both_ conditions to be true regardless of ordering.
  yield all({
    action: take(FIRST_EVAL_REDUX_ACTIONS),
    start: call(function* () {
      yield call(worker.shutdown);
      yield call(worker.start);
    }),
  });

  widgetTypeConfigMap = WidgetFactory.getWidgetTypeConfigMap();

  const evaluateActionChannel: Channel<ReduxAction<unknown>> =
    yield actionChannel(EVALUATE_REDUX_ACTIONS, buffers.expanding());

  const widgets: CanvasWidgetsReduxState = yield select(getWidgets);
  const widgetMeta: MetaState = yield select(getWidgetsMeta);
  const apis: Record<string, ApiV1> = yield select(selectAllV1Apis);
  const apisV2: ReturnType<typeof selectAllV2Apis> =
    yield select(selectAllV2Apis);
  const initReplayState = {
    apis,
    apisV2,
    widgets,
    shouldReplay: true,
    clearReplayStack: false,
    widgetMeta,
  } as ReplayState;
  try {
    yield call(evaluateTreeSaga, initReplayState, true);
  } catch (e) {
    // The evaluateTreeSaga previously had a silent crash when creating a blank workflow,
    // so now we log an error and leave the evaluator in a state where it can re-start later.
    logger.error(e);
    yield call(worker.shutdown);
    return;
  }

  let lastEvalAttempt = moment();
  while (true) {
    // Wait for at least one action to be taken
    const action: ReduxAction<unknown> = yield take(evaluateActionChannel);
    const actionsQueued: ReduxAction<unknown>[] = yield flush(
      evaluateActionChannel,
    );

    if (
      action.type === stopEvaluation.type ||
      actionsQueued.some((action) => action.type === stopEvaluation.type)
    ) {
      yield call(worker.shutdown);
      return;
    }

    while (true) {
      // We're now in the leading debounced state, all actions from
      // evaluateActionChannel will be ignored apart from STOP_EVALUATION.
      // This loop will run until delay is expired or STOP_EVALUATION is taken.
      // Leading means that we evaluate on the leading edge of the timeout.

      yield put({ type: ReduxActionTypes.START_EVALUATE_QUEUE });
      const actionsDebounce = EVALUATE_DEBOUNCE_DELAY[action.type];
      const msSinceLastEval = moment().diff(lastEvalAttempt);
      const debounceLength = Math.max(
        actionsDebounce === NO_DEBOUNCE ? 0 : EVALUATE_MINIMUM_DEBOUNCE_DELAY,
        actionsDebounce - msSinceLastEval,
      );

      const {
        willEval,
        latestAction,
      }: {
        willEval?: boolean;
        latestAction?: ReduxAction<unknown>;
      } = yield race({
        willEval: debounceLength > 0 ? delay(debounceLength) : true,
        latestAction: take(evaluateActionChannel),
      });

      if (willEval) {
        yield call(reevaluateTree, {
          shouldReplay: (action.payload as any)?.shouldReplay,
          clearReplayStack: (action.payload as any)?.clearReplayStack,
        });
        lastEvalAttempt = moment();
        break;
      } else {
        if (latestAction) {
          actionsQueued.push(latestAction);
        }
        lastEvalAttempt = moment();
      }

      if (latestAction && latestAction.type === stopEvaluation.type) {
        yield call(worker.shutdown);
        return;
      }
    }
  }
}

export default function* evaluationSagaListeners() {
  while (true) {
    // always be listening for start_evaluation events
    yield take(startEvaluation.type);
    while (true) {
      try {
        const { stopEval } = yield race({
          run: call(evaluationChangeListenerSaga),
          stopEval: take(stopEvaluation.type),
          restartEval: take(restartEvaluation.type),
        });
        if (stopEval) {
          yield call(worker.shutdown);
        }
        // if evaluationChangeListenerSaga finished successfully, break out
        break;
      } catch (e) {
        logger.error(e);
        console.error("[EvaluationsSaga]", e);
      }
    }
  }
}
